<script setup lang="ts">
import type {Component} from 'vue'
import {computed, ref, useAttrs} from 'vue'
import {useI18n} from 'vue-i18n'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import FloatingTooltipComponent from '@toolify/client/src/modules/modals/components/FloatingTooltipComponent.vue'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {IPropertyValueStrategyPayload} from '@toolify/client/src/modules/tableItems/types/IPropertyValueStrategyPayload'
import {
  DatabasePropertyValueEditStrategyByTypeMap,
} from '@toolify/client/src/modules/databases/maps/DatabasePropertyValueEditStrategyByTypeMap'
import {
  DatabasePropertyValueStrategyByTypeMap,
} from '@toolify/client/src/modules/databases/maps/DatabasePropertyValueReadStrategyByTypeMap'
import {
  getIsMaskedCellRevealActive,
  getMaskedCellRevealErrorMessageKey,
  getMaskedDatabaseCellKey,
  useMaskedDatabaseProperties,
} from '@toolify/client/src/modules/databases/composables/useMaskedDatabaseProperties'
import {DEFAULT_MASKED_REVEAL_LIMIT} from '@toolify/server/src/services/DatabasePropertyService/constants/maskedRevealLimit'
import {useDatabaseItemApiStore} from '@toolify/client/src/stores/api/DatabaseItemApiStore/useDatabaseItemApiStore'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'

defineOptions({
  inheritAttrs: false,
})

interface Props {
  payload?: Partial<IPropertyValueStrategyPayload> | null
  relativeToDate?: Date
  cellPayload?: unknown
  isFocused?: boolean
  isInEditMode?: boolean
  isInPreviewMode?: boolean
  isCellHidden?: boolean
  isRevealable?: boolean
}

const DOT_COUNT = 6

const props = withDefaults(defineProps<Props>(), {
  payload: null,
  relativeToDate: null,
  cellPayload: null,
  isFocused: false,
  isInEditMode: false,
  isInPreviewMode: false,
  isCellHidden: false,
  isRevealable: true,
})

const attrs = useAttrs()
const {t} = useI18n()
const messages = useMessages()
const itemApiStore = useDatabaseItemApiStore()
const authStore = useAuthStore()
const {
  maskedCellRevealsByKey,
  revealMaskedDatabaseItemCell,
  updateRevealedMaskedCellValue,
  hideMaskedDatabaseItemCell,
} = useMaskedDatabaseProperties()

const dots = Array.from({length: DOT_COUNT}, (_, index) => index)

const isRevealing = ref(false)

const itemId = computed(() => props.payload?.itemId ?? null)
const propertyId = computed(() => props.payload?.propertyId ?? props.payload?.property?.id ?? null)
const valueType = computed(() => props.payload?.property?.valueType ?? null)
const maskedRevealLimit = computed(() => props.payload?.property?.maskedRevealLimit ?? DEFAULT_MASKED_REVEAL_LIMIT)
const canReveal = computed(() => {
  return props.isRevealable
    && !!itemId.value
    && !!propertyId.value
    && authStore.currentEntity?.entityType !== EntityType.Guest
})

const activeReveal = computed(() => {
  if(!canReveal.value) {
    return null
  }
  const reveal = maskedCellRevealsByKey.value.get(getMaskedDatabaseCellKey(itemId.value, propertyId.value))
  return reveal && getIsMaskedCellRevealActive(reveal.revealUntil) ? reveal : null
})

const isRevealed = computed(() => !!activeReveal.value)

const innerStrategyComponent = computed<Component | null>(() => {
  if(DatabasePropertyValueEditStrategyByTypeMap.has(valueType.value) && (props.isInEditMode || props.isInPreviewMode)) {
    return DatabasePropertyValueEditStrategyByTypeMap.get(valueType.value)
  }
  return DatabasePropertyValueStrategyByTypeMap.get(valueType.value) ?? null
})

const innerPayload = computed<IPropertyValueStrategyPayload>(() => ({
  ...(props.payload as IPropertyValueStrategyPayload),
  value: activeReveal.value?.value ?? null,
}))

function hide() {
  if(canReveal.value) {
    hideMaskedDatabaseItemCell(itemId.value, propertyId.value)
  }
}

async function reveal() {
  if(!canReveal.value || isRevealing.value) {
    return
  }
  isRevealing.value = true
  try {
    const {value, revealedUntil} = await itemApiStore.revealMaskedCellValue(itemId.value, propertyId.value)
    revealMaskedDatabaseItemCell({
      databaseItemId: itemId.value,
      databasePropertyId: propertyId.value,
      revealUntil: new Date(revealedUntil),
      value,
      maxRevealedCells: maskedRevealLimit.value,
    })
  } catch(error) {
    messages.error(t(getMaskedCellRevealErrorMessageKey(error)))
  } finally {
    isRevealing.value = false
  }
}

function onUpdateValueLocal(fullValue: unknown) {
  if(isRevealed.value) {
    updateRevealedMaskedCellValue(itemId.value, propertyId.value, fullValue)
  }
}
</script>

<template>
  <div
    class="masked-item-value"
    :class="{'is-revealed': isRevealed}"
    :title="isRevealed ? null : t('database.maskedCell.maskedValue')"
  >
    <div v-if="isRevealed && innerStrategyComponent" class="revealed-value">
      <Component
        :is="innerStrategyComponent"
        :payload="innerPayload"
        :relativeToDate="props.relativeToDate"
        :cellPayload="props.cellPayload || {}"
        :isFocused="props.isFocused"
        :isInEditMode="props.isInEditMode"
        :isInPreviewMode="props.isInPreviewMode"
        :isCellHidden="props.isCellHidden"
        v-bind="attrs"
        @updateValueLocal="onUpdateValueLocal"
      />
    </div>
    <div v-else class="dots">
      <span v-for="dot in dots" :key="dot" class="dot"/>
    </div>
    <FloatingTooltipComponent v-if="canReveal" placement="top" class="toggle-button-wrapper">
      <ButtonComponent
        :iconNameType="isRevealed ? IconNameType.EyeSlash : IconNameType.Eye"
        :iconWeightType="IconWeightType.Solid"
        :style="StyleVariantType.ClearClear"
        :color="ColorVariantType.Neutral"
        :size="SizeVariantType.Small"
        :isLoading="isRevealing"
        :isDisabled="isRevealing"
        class="toggle-button"
        @click.stop="isRevealed ? hide() : reveal()"
      />
      <template #tooltip>
        {{isRevealed ? t('database.maskedCell.hide') : t('database.maskedCell.show')}}
      </template>
    </FloatingTooltipComponent>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../../../../styles/variables' as variables;

  .masked-item-value {
    position: relative;
    display: flex;
    align-items: center;
    gap: map-get(variables.$spacing, space-1);
    height: 100%;
    width: 100%;
    min-width: 0;
  }

  .revealed-value {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    height: 100%;
  }

  .dots {
    display: flex;
    align-items: center;
    gap: map-get(variables.$spacing, space-1);
    flex: 1;
    min-width: 0;
  }

  .dot {
    width: 6px;
    height: 6px;
    flex: none;
    border-radius: 50%;
    background: map-get(variables.$colors, content-dim);
  }

  .toggle-button-wrapper {
    position: absolute;
    right: 3px;
    top: 50%;
    transform: translateY(-50%);
  }

  .masked-item-value.is-revealed .revealed-value :deep(.copy-icon-wrapper) {
    right: calc(3px + #{map-get(variables.$componentSizes, small)} + #{map-get(variables.$spacing, space-0-5)}) !important;
  }

  @media (hover: hover) {
    .masked-item-value.is-revealed .toggle-button-wrapper {
      visibility: hidden;
    }

    .masked-item-value.is-revealed:hover .toggle-button-wrapper {
      visibility: visible;
    }
  }
</style>
